import { Link } from "react-router-dom";
import { siteInfo } from "@/mocks/site";
import { useSEO } from "@/hooks/useSEO";

const faqs = [
  {
    q: "如何在站内查找影视作品？",
    a: "你可以在首页或顶部导航的搜索框中输入片名、题材或地区关键词，系统会跳转到搜索结果页并展示匹配的欧美影视资料。",
  },
  {
    q: "筛选功能支持哪些维度？",
    a: "分类页支持按影视类型（剧情、动作、科幻等）、地区（美国、英国、法国等）与上映年份进行多选筛选，并可切换最新、热门、评分排序。",
  },
  {
    q: "收藏功能会同步到其他设备吗？",
    a: "不会。收藏功能仅在当前浏览器本地保存用于演示，刷新页面后依然保留，但不会上传到服务器，也不会跨设备同步。",
  },
  {
    q: "网站提供在线观看或下载吗？",
    a: "不提供。本站为影视资料导航与展示演示站点，仅呈现作品信息与分类目录，不含播放、下载或付费功能。",
  },
];

export default function About() {
  useSEO({
    title: "关于我们 - 欧美一级视频",
    description:
      "了解欧美一级视频的网站定位、服务范围、联系方式与常见问题，我们专注于欧美电影与电视剧的资料导航与展示。",
    keywords: "关于欧美一级视频,欧美影视导航,影视资料站,联系方式,常见问题",
  });

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-8 md:px-6 md:py-12">
      <nav className="mb-4 flex items-center gap-1.5 text-xs text-foreground-600">
        <Link to="/" className="cursor-pointer hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900">关于我们</span>
      </nav>

      <h1 className="text-3xl font-black text-foreground-950 md:text-4xl">关于我们</h1>
      <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
        {siteInfo.intro}
      </p>

      <section className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { icon: "ri-movie-2-line", label: "精选资料", value: "欧美电影与电视剧结构化信息" },
          { icon: "ri-filter-3-line", label: "多维筛选", value: "类型 / 地区 / 年份组合检索" },
          { icon: "ri-heart-3-line", label: "本地收藏", value: "浏览器本地保存，刷新不丢失" },
        ].map((item) => (
          <div
            key={item.label}
            className="flex flex-col gap-2 rounded-lg border border-background-200 bg-background-50 p-4"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-600">
              <i className={`${item.icon} text-xl`}></i>
            </span>
            <span className="text-base font-semibold text-foreground-950">{item.label}</span>
            <span className="text-xs leading-relaxed text-foreground-600">{item.value}</span>
          </div>
        ))}
      </section>

      <section className="mt-10">
        <h2 className="mb-4 flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
          <span className="h-5 w-1 rounded-full bg-accent-500"></span>
          联系方式
        </h2>
        <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
          <p className="text-sm leading-relaxed text-foreground-700">
            本站暂不提供在线表单或实时客服。如需反馈内容问题、合作咨询或提出建议，欢迎通过邮件联系我们，
            我们会在收到邮件后尽快回复。
          </p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <div className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-4 py-3">
              <i className="ri-mail-send-line text-lg text-primary-500"></i>
              <div className="flex flex-col">
                <span className="text-xs text-foreground-500">邮箱</span>
                <span className="break-all text-sm font-medium text-foreground-900">
                  {siteInfo.email}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-4 py-3">
              <i className="ri-time-line text-lg text-primary-500"></i>
              <div className="flex flex-col">
                <span className="text-xs text-foreground-500">回复时间</span>
                <span className="text-sm font-medium text-foreground-900">
                  {siteInfo.workingHours}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="mb-4 flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
          <span className="h-5 w-1 rounded-full bg-accent-500"></span>
          常见问题
        </h2>
        <div className="flex flex-col gap-3">
          {faqs.map((item) => (
            <div
              key={item.q}
              className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
            >
              <h3 className="flex items-start gap-2 text-base font-semibold text-foreground-950">
                <i className="ri-question-line mt-0.5 text-accent-500"></i>
                {item.q}
              </h3>
              <p className="mt-2 pl-6 text-sm leading-relaxed text-foreground-700">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-10 flex flex-wrap gap-3 border-t border-background-200 pt-6">
        <Link
          to="/"
          className="cursor-pointer rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
        >
          返回首页
        </Link>
        <Link
          to="/category/movie"
          className="cursor-pointer rounded-md border border-primary-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-primary-600 hover:bg-primary-50"
        >
          浏览影视分类
        </Link>
      </div>
    </div>
  );
}